import { CheckCircle2, AlertTriangle, Ban } from 'lucide-react'
import type { Option } from '@/types'
import { formatCurrency } from '@/lib/pricing'
import { getAssetUrl } from '@/lib/api'

interface Props {
  option: Option
  isSelected: boolean
  isDisabled: boolean
  warning?: string
  isBlocked?: boolean
  blockMessage?: string
  onToggle: () => void
}

export function OptionCard({ option, isSelected, isDisabled, warning, isBlocked, blockMessage, onToggle }: Props) {
  const cantSelect = isDisabled || isBlocked

  return (
    <div
      className={`relative bg-white rounded-xl border-2 transition-all overflow-hidden ${
        cantSelect
          ? 'border-slate-100 opacity-50 cursor-not-allowed'
          : isSelected
          ? 'border-brand-500 shadow-selected cursor-pointer'
          : 'border-slate-200 hover:border-slate-300 shadow-card hover:shadow-card-hover cursor-pointer'
      }`}
      onClick={() => !cantSelect && onToggle()}
      title={blockMessage ?? (isDisabled ? 'Not available with your current selections' : undefined)}
    >
      {/* Image */}
      {option.image_url && (
        <div className="aspect-video bg-slate-100 overflow-hidden">
          <img src={getAssetUrl(option.image_url)} alt={option.name} className="w-full h-full object-cover" />
        </div>
      )}

      {/* Selected / blocked indicator */}
      {isSelected && !cantSelect && (
        <div className="absolute top-2.5 right-2.5 bg-brand-500 rounded-full p-0.5">
          <CheckCircle2 className="h-4 w-4 text-white fill-white" />
        </div>
      )}
      {cantSelect && (
        <div className="absolute top-2.5 right-2.5 bg-slate-300 rounded-full p-0.5">
          <Ban className="h-4 w-4 text-white" />
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h4 className="font-semibold text-slate-900 text-sm leading-tight">{option.name}</h4>
          <div className="text-sm font-bold text-slate-800 shrink-0">
            {option.price === 0 ? 'Included' : `+${formatCurrency(option.price)}`}
          </div>
        </div>

        <p className="text-xs text-slate-500 leading-relaxed mb-3 line-clamp-3">{option.description}</p>

        {warning && (
          <div className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-2 mb-3">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>{warning}</span>
          </div>
        )}

        <div
          className={`text-center text-xs font-medium py-1.5 rounded-lg transition-colors ${
            cantSelect
              ? 'bg-slate-100 text-slate-400'
              : isSelected
              ? 'bg-brand-500 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          {cantSelect ? 'Unavailable' : isSelected ? 'Added ✓' : 'Add to Build'}
        </div>
      </div>
    </div>
  )
}
